import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  UserGroupIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import { getImageUrlWithEnv } from '../utils/imageUrl';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const SocialStaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const response = await fetch(getApiUrl('/api/social-staff/admin'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const staffData = await response.json();
        setStaff(staffData);
      } else {
        console.error('Error loading staff:', response.statusText);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading staff:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ნამდვილად გსურთ ამ პერსონალის წაშლა?')) {
      try {
        // Get token from the same source as apiService
        const storedUser = localStorage.getItem('fciveria_admin_user');
        const token = storedUser ? JSON.parse(storedUser).token : null;
        
        if (!token) {
          console.error('No auth token found');
          return;
        }
        
        const response = await fetch(getApiUrl(`/api/social-staff/${id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          loadStaff();
        } else {
          console.error('Error deleting staff:', response.statusText);
        }
      } catch (error) {
        console.error('Error deleting staff:', error);
      }
    }
  };

  const toggleActive = async (staffMember) => {
    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      const response = await fetch(getApiUrl(`/api/social-staff/${staffMember.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...staffMember,
          isActive: !staffMember.isActive
        })
      });
      
      if (response.ok) {
        loadStaff();
      } else {
        console.error('Error updating staff:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating staff:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title="სოც მედია პერსონალის მართვა"
        subtitle="მართეთ სოც მედია გუნდის წევრები"
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/social-staff/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          ახალი პერსონალი
        </button>
      </AdminHeader>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {staff.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">პერსონალი არ არის</h3>
              <p className="mt-1 text-sm text-gray-500 font-georgian">დაიწყეთ ახალი პერსონალის დამატებით</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/_panel-7c39f8/social-staff/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 font-georgian"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  ახალი პერსონალი
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {staff.map((staffMember) => (
                <div key={staffMember.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                  <div className="relative">
                    {staffMember.photoUrl ? (
                      <img
                        src={getImageUrlWithEnv(staffMember.photoUrl)}
                        alt={staffMember.name}
                        className="w-full h-48 object-cover"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                        <UserIcon className="h-16 w-16 text-white" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        staffMember.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {staffMember.isActive ? 'აქტიური' : 'არ არის აქტიური'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-1">
                      {staffMember.name}
                    </h3>
                    <p className="text-sm text-blue-600 font-georgian mb-3">
                      {staffMember.position}
                    </p>
                    
                    <div className="space-y-2 text-sm text-gray-600 font-georgian mb-4">
                      {staffMember.age && (
                        <p>ასაკი: {staffMember.age} წელი</p>
                      )}
                      {staffMember.experienceYears && (
                        <p>გამოცდილება: {staffMember.experienceYears} წელი</p>
                      )}
                      {staffMember.phone && (
                        <div className="flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          <a 
                            href={`tel:${staffMember.phone}`}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            {staffMember.phone}
                          </a>
                        </div>
                      )}
                      {staffMember.email && (
                        <div className="flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          <a 
                            href={`mailto:${staffMember.email}`}
                            className="text-green-600 hover:text-green-800 transition-colors"
                          >
                            {staffMember.email}
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {staffMember.description && (
                      <p className="text-xs text-gray-500 font-georgian mb-4 line-clamp-3">
                        {staffMember.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleActive(staffMember)}
                        className={`text-xs px-3 py-1 rounded font-georgian ${
                          staffMember.isActive
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : 'bg-green-100 text-green-600 hover:bg-green-200'
                        }`}
                      >
                        {staffMember.isActive ? 'დეაქტივაცია' : 'აქტივაცია'}
                      </button>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/_panel-7c39f8/social-staff/${staffMember.id}/edit`)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(staffMember.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">სულ პერსონალი</p>
                <p className="text-2xl font-bold text-gray-900">{staff.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">აქტიური</p>
                <p className="text-2xl font-bold text-gray-900">
                  {staff.filter(s => s.isActive).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">#</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">პოზიციები</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(staff.map(s => s.position)).size}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialStaffManagement;










